import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { StaticFieldOption } from './staticFieldOptions';

export interface MappingResultGroup {
  label: string;
  options: StaticFieldOption[];
}

interface Props {
  /** The search input the list hangs under. */
  anchor: HTMLElement | null;
  groups: MappingResultGroup[];
  selectedValue?: string;
  /** Index into the flattened options, driven by the input's arrow keys. */
  activeIndex: number;
  onPick: (value: string) => void;
}

const GAP = 4;
const EDGE = 16;
const MAX_WIDTH = 640;
const MAX_HEIGHT = 320;
// Below this much room above the input, open downward instead.
const MIN_ROOM_ABOVE = 160;

interface Position {
  left: number;
  minWidth: number;
  maxHeight: number;
  /** Set when opening upward: distance from the viewport bottom. */
  bottom?: number;
  /** Set when opening downward. */
  top?: number;
}

const samePosition = (a: Position | null, b: Position) =>
  !!a &&
  a.left === b.left &&
  a.minWidth === b.minWidth &&
  a.maxHeight === b.maxHeight &&
  a.top === b.top &&
  a.bottom === b.bottom;

/**
 * The Field mapping search matches, as a popup over the search box.
 *
 * Portalled to <body> and positioned `fixed` from the input's rect, so it can
 * be wider than the field card -- the group paths ("Regular Forms › Inspection
 * › Permit › Project › Manager") are what tell 14 identical "Trade" rows apart,
 * and the card is too narrow to show them. Sized to its content like the
 * browser's own dropdown, then nudged left if that would run off the screen.
 *
 * Opens ABOVE the input: the field card usually sits low in the builder
 * panel, and a list hanging below it ran off the bottom of the screen. It
 * only drops below when there is too little room above.
 *
 * Items take the pick on mousedown with preventDefault, so the input never
 * loses focus mid-click (losing it is what closes the list).
 */
const MappingSearchResults = ({
  anchor,
  groups,
  selectedValue,
  activeIndex,
  onPick,
}: Props) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<Position | null>(null);

  const place = React.useCallback(() => {
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = listRef.current?.offsetWidth || rect.width;
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE));
    const roomAbove = rect.top - GAP - EDGE;
    const roomBelow = window.innerHeight - rect.bottom - GAP - EDGE;
    const openUp = roomAbove >= MIN_ROOM_ABOVE || roomAbove >= roomBelow;
    const next: Position = openUp
      ? {
        left,
        minWidth: rect.width,
        maxHeight: Math.min(MAX_HEIGHT, roomAbove),
        bottom: window.innerHeight - rect.top + GAP,
      }
      : {
        left,
        minWidth: rect.width,
        maxHeight: Math.min(MAX_HEIGHT, roomBelow),
        top: rect.bottom + GAP,
      };
    // Runs after every render, so only a real change may set state --
    // otherwise each set would render again, forever.
    setPos(prev => (samePosition(prev, next) ? prev : next));
  }, [anchor]);

  // Re-measured after every render: the width follows the matches, which
  // change on each keystroke.
  React.useLayoutEffect(place);

  React.useEffect(() => {
    // Capture, so scrolling the builder panel (not just the window) follows.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  React.useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  let index = -1;
  return ReactDOM.createPortal(
    <List
      ref={listRef}
      role="listbox"
      style={{
        // Off-screen until the first measurement places it.
        top: pos ? pos.top : -9999,
        bottom: pos?.bottom,
        left: pos?.left ?? -9999,
        minWidth: pos?.minWidth,
        maxHeight: pos?.maxHeight ?? MAX_HEIGHT,
        maxWidth: `min(${MAX_WIDTH}px, calc(100vw - ${EDGE * 2}px))`,
      }}
    >
      {groups.map(g => (
        <div key={g.label} role="group" aria-label={g.label}>
          <GroupLabel>{g.label}</GroupLabel>
          {g.options.map(o => {
            index += 1;
            const isActive = index === activeIndex;
            return (
              <Item
                key={o.value}
                role="option"
                aria-selected={o.value === selectedValue}
                data-active={isActive}
                $active={isActive}
                $selected={o.value === selectedValue}
                onMouseDown={e => {
                  e.preventDefault();
                  onPick(o.value);
                }}
              >
                {o.label}
              </Item>
            );
          })}
        </div>
      ))}
    </List>,
    document.body,
  );
};

/** The options in the order the list shows them -- what the arrow keys walk. */
export const flattenResults = (groups: MappingResultGroup[]): StaticFieldOption[] =>
  groups.reduce<StaticFieldOption[]>((all, g) => all.concat(g.options), []);

const List = styled.div`
  position: fixed;
  z-index: 1300;
  box-sizing: border-box;
  width: max-content;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Arial, Roboto, sans-serif;
  font-size: 0.8125rem;
`;

const GroupLabel = styled.div`
  padding: 6px 12px 2px;
  font-weight: 700;
  color: var(--mainText);
  white-space: nowrap;
`;

const Item = styled.div<{ $active: boolean; $selected: boolean }>`
  padding: 4px 12px 4px 28px;
  color: ${({ $active }) => ($active ? 'var(--neutral-0)' : 'var(--mainText)')};
  background: ${({ $active, $selected }) => {
    if ($active) return 'var(--primary-600)';
    if ($selected) return 'var(--primary-50)';
    return 'transparent';
  }};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--neutral-100)')};
  }
`;

export default MappingSearchResults;
